<template>
  <header class="header">
    <!--1A: Portal header -->
    <div class="portal-header portal-header-compact">
      <div class="container portal-header-inner">
        <AppIcon />
        <h1 :style="configStore.headlineStyle">Statens Administrative Stamdata</h1>
        <button
          class="function-link button-menu-open js-menu-open ml-auto d-print-none"
          aria-haspopup="menu"
          title="Åben mobil menu"
        >
          <svg class="icon-svg" focusable="false" aria-hidden="true">
            <use xlink:href="#menu" />
          </svg>
          Menu
        </button>

        <!-- 1B: Portal header: info + actions-->
        <div class="portal-info">
          <p class="user">
            <strong class="username weight-semibold">{{
              profileStore.user.userName
            }}</strong>
            <br />
            {{ profileStore.user.organizationName }}
          </p>
          <router-link
            v-if="
              profileStore.isAuthenticated &&
              profileStore.user.email.includes('kundedemo')
            "
            class="button button-secondary button-sm alert-leave d-print-none"
            to="/logout"
          >
            Log af
          </router-link>
        </div>
      </div>
    </div>

    <div class="overlay"></div>
    <nav class="nav">
      <!-- collapsible-->
      <button
        class="function-link button-menu-close js-menu-close"
        title="Luk mobil menu"
      >
        <svg class="icon-svg" aria-hidden="true">
          <use xlink:href="#close"></use>
        </svg>
        Luk
      </button>

      <!-- 3: Main navigation-->
      <div class="navbar navbar-primary">
        <!--3A: Main navigation-->
        <div class="navbar-inner container">
          <ul class="nav-primary">
            <router-link to="/regnskabsstruktur" custom v-slot="{ navigate }">
              <li
                @click="navigate"
                :class="{ current: $route.path === '/regnskabsstruktur' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Regnskabsstruktur</span>
                </a>
              </li>
            </router-link>
            <router-link to="/kontoplan" custom v-slot="{ navigate }">
              <li
                @click="navigate"
                :class="{ current: $route.path === '/kontoplan' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Kontoplan</span>
                </a>
              </li>
            </router-link>
            <router-link to="/finanslovsstruktur" custom v-slot="{ navigate }">
              <li
                @click="navigate"
                :class="{ current: $route.path === '/finanslovsstruktur' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Finanslovsstruktur</span>
                </a>
              </li>
            </router-link>

            <router-link
              to="/create-element-overview"
              custom
              v-slot="{ navigate }"
            >
              <li
                @click="navigate"
                :class="{ current: $route.path === '/create-element-overview' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Opret nyt element</span>
                </a>
              </li>
            </router-link>
            <router-link
              v-if="hasAccessToProcessTab"
              to="/processoverview"
              custom
              v-slot="{ navigate }"
            >
              <li
                @click="navigate"
                :class="{ current: $route.path === '/processoverview' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Processtyring</span>
                </a>
              </li>
            </router-link>
            <router-link to="/perioder" custom v-slot="{ navigate }">
              <li
              @click="navigate"
                :class="{ current: $route.path === '/perioder' }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Perioder</span>
                </a>
              </li>
            </router-link>
            <router-link
              v-if="hasAccessKontobroerTab"
              to="/kontobroer"
              custom
              v-slot="{ navigate }"
            >
              <li @click="navigate" :class="{ current: $route.path === '/kontobroer' }">
                <a href="#" class="nav-link">
                  <span role="link">Kontobroer</span>
                </a>
              </li>
            </router-link>
            <router-link
              v-if="hasAccessActivitiesTab"
              to="/aktiviteter"
              custom
              v-slot="{ navigate }"
            >
              <li @click="navigate" :class="{ current: $route.path === '/aktiviteter' }">
                <a href="#" class="nav-link">
                  <span role="link">Aktiviteter</span>
                </a>
              </li>
            </router-link>
            <router-link
              v-if="hasAccessLoengrupperTab"
              to="/loengrupper"
              custom
              v-slot="{ navigate }"
            >
              <li
                @click="navigate"
                :class="{
                  current: $route.path === '/loengrupper',
                }"
              >
                <a href="#" class="nav-link">
                  <span role="link">Løngrupper</span>
                </a>
              </li>
            </router-link>
            <li :class="{ current: $route.path.startsWith('/rapport/') }" class="rapport-menu-item">
              <a href="#" class="nav-link rapport-menu-trigger" @click.prevent>
                <span role="link">Rapport</span>
              </a>
              <ul class="rapport-submenu">
                <li>
                  <router-link to="/rapport/reg" class="nav-link-sub"
                    >Regnskabsstruktur</router-link
                  >
                </li>
                <li>
                  <router-link
                    to="/rapport/reg-bogfoeringskredse-delregnskaber-underkonti"
                    class="nav-link-sub"
                    >Bogføringskredse, delregnskaber og underkonti</router-link
                  >
                </li>
                <li>
                  <router-link to="/rapport/fl" class="nav-link-sub"
                    >Finanslovsstruktur</router-link
                  >
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
      <!-- 3: Main navigation end-->
    </nav>
    <!-- collapsible nav end-->
  </header>
</template>

<script setup lang="ts">
import AppIcon from "@/components/layouts/AppIcon.vue";
import { useConfigStore } from "@/stores/config";
import { useProfileStore } from "@/stores/profile";
import { computed } from "vue";

const profileStore = useProfileStore();
const configStore = useConfigStore();

//Users with either SADAProcesStyring, SADARettighederNiveau1, SADARettighederNiveau2 orSADARettighederAlle
//can see or click processoverview tab
const hasAccessToProcessTab = computed(() => {
  return profileStore.accessibleCommand?.hasAccessToProcessTab;
});
const hasAccessActivitiesTab = computed(() => {
  return profileStore.accessibleCommand?.hasAccessActivitiesTab;
});
const hasAccessLoengrupperTab = computed(() => {
  return profileStore.accessibleCommand?.hasAccessLoengrupperTab;
});
const hasAccessKontobroerTab = computed(() => {
  return profileStore.accessibleCommand?.hasAccessKontobroerTab;
});
</script>

<style lang="scss" scoped>
.oes-logo {
  background-image: url(../../../src/assets/oes_logo_rgb_dk.svg);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center left;
  display: inline-block;
  height: 60px;
  width: 200px;
  max-width: 60%;
}

.rapport-menu-item {
  position: relative;
}

.rapport-menu-trigger {
  align-items: center;
  display: inline-flex;
  gap: 0.5rem;
}

.rapport-submenu {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  opacity: 0;
  padding: 0.5rem;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  transform: translateY(0.4rem);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  visibility: hidden;
  background: #ffffff;
  border: 1px solid #d8e2dc;
  border-radius: 0.9rem;
  box-shadow: 0 1rem 2.5rem rgba(5, 54, 36, 0.14);
  min-width: 12rem;
  z-index: 10;
}

.rapport-submenu::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: -0.5rem;
  height: 0.5rem;
}

.rapport-submenu li::marker {
  content: none;
}

.rapport-submenu > li::before,
.rapport-submenu li::before {
  content: "" !important;
  display: none !important;
  padding-right: 0 !important;
}

.rapport-menu-item:hover .rapport-submenu,
.rapport-menu-item:focus-within .rapport-submenu {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
  visibility: visible;
}

.nav-link-sub {
  display: block;
  border-radius: 0.65rem;
  color: #0f5132;
  font-weight: 600;
  padding: 0.75rem 1rem;
  text-decoration: none;
  transition: background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
  white-space: nowrap;
}

.nav-link-sub:hover,
.nav-link-sub:focus-visible {
  background: #edf7f1;
  color: #0a3d27;
  outline: none;
  transform: translateX(0.15rem);
}

.nav-link-sub.router-link-active {
  background: #dff0e6;
  color: #0a3d27;
}
</style>
